import { useCallback, useEffect, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Feature, Subscription } from '../../models';
import { fromAccounts, fromAuth, useAppDispatch } from '../../store';
import { Page } from '../../components/page';
import ButtonToLicenseManagement from '../payment-status/ButtonToLicenseManagement';
import {
  SubscriptionTableRow,
  SUBSCRIPTION_ROW_GRID,
} from './component/subscription-table/SubscriptionTableRow';
import { useGetTrackingCookies } from '../../hook';
import { useLaunchDarkly } from '../../launchdarkly';
import { withSubscriptions } from '../../utils/analytics';
import { sendTrackingData, TrackingPayload } from '../../utils/utils';
import { isTrialSubscription } from './subscriptionTrial';

const useStyles = makeStyles(theme => ({
  pageHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(3),
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  pageTitle: {
    margin: 0,
    fontSize: 30,
    fontWeight: 500,
    letterSpacing: '-0.02em',
    color: '#16141D',
  },
  accountLine: {
    marginTop: theme.spacing(1.25),
    fontSize: 13,
    lineHeight: 1.5,
    color: '#73718A',
  },
  accountName: {
    fontWeight: 600,
    color: '#16141D',
  },
  tableCard: {
    backgroundColor: '#fff',
    border: '1px solid #E1E7E4',
    borderRadius: 16,
    overflow: 'hidden',
  },
  tableScroll: {
    overflowX: 'auto',
  },
  tableInner: {
    minWidth: 760,
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: SUBSCRIPTION_ROW_GRID,
    columnGap: theme.spacing(1.75),
    alignItems: 'center',
    padding: theme.spacing(1.75, 3.25, 1.5),
    borderBottom: '1px solid #EEF2F0',
  },
  tableHeaderLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: '#6A6A6A',
  },
}));

const groupSubscriptionsByFeature = (subscriptions: Subscription[]): Subscription[][] => {
  const groups = new Map<Feature, Subscription[]>();
  subscriptions.forEach(subscription => {
    const group = groups.get(subscription.feature) ?? [];
    group.push(subscription);
    groups.set(subscription.feature, group);
  });

  return Array.from(groups.values());
};

const getPrimarySubscription = (subscriptions: Subscription[]) => (
  subscriptions.find(subscription => !isTrialSubscription(subscription))
    ?? subscriptions[0]
);

// Accounts flagged as direct-sale on LaunchDarkly render every subscription as
// contract-managed (locked auto-renew + contract renewal note). The flag is either
// a boolean served per account by LD targeting rules, or a JSON array of account IDs.
const isDirectSaleManagedAccount = (
  accountId: number,
  directSaleAccountIds?: boolean | Array<number | string>,
) => {
  if (!accountId) {
    return false;
  }
  if (typeof directSaleAccountIds === 'boolean') {
    return directSaleAccountIds;
  }
  return (directSaleAccountIds ?? []).some(id => Number(id) === accountId);
};

const SubscriptionV3Content = (
  { trueSubscriptions, accountId }: { trueSubscriptions: Subscription[]; accountId: number },
) => {
  const classes = useStyles();
  const { flags } = useLaunchDarkly();
  const trackingCookies = useGetTrackingCookies();
  const trackedViewAccountRef = useRef<number>();
  const subscriptionGroups = useMemo(
    () => groupSubscriptionsByFeature(trueSubscriptions),
    [trueSubscriptions],
  );
  const accountManaged = isDirectSaleManagedAccount(accountId, flags?.directSaleAccountIds);
  const trackSubscriptionAction = useCallback((payload: TrackingPayload) => {
    if (!accountId) {
      return;
    }

    sendTrackingData(
      'subscription_page_all_action',
      accountId,
      undefined,
      {
        field: payload.field,
        object: payload.object,
        action: payload.action,
        plan_flow: 'true_bundle',
        ...payload,
        ...trackingCookies,
      },
    );
  }, [accountId, trackingCookies]);

  useEffect(() => {
    if (!accountId
      || trackedViewAccountRef.current === accountId
      || trueSubscriptions.length === 0) {
      return;
    }

    trackedViewAccountRef.current = accountId;
    sendTrackingData(
      'view_subscription_management',
      accountId,
      undefined,
      withSubscriptions(
        {
          page: 'subscription_management',
          plan_flow: 'true_bundle',
          ...trackingCookies,
        },
        trueSubscriptions,
      ),
    );
  }, [accountId, trueSubscriptions, trackingCookies]);

  if (trueSubscriptions.length === 0) {
    return null;
  }

  return (
    <Box paddingBottom={2}>
      <Box className={classes.tableCard}>
        <Box className={classes.tableScroll}>
          <Box className={classes.tableInner}>
            <Box className={classes.tableHeader}>
              <Typography className={classes.tableHeaderLabel}>
                <FormattedMessage id="page.subscription.v3.product.column" />
              </Typography>
              <Typography className={classes.tableHeaderLabel}>
                <FormattedMessage id="page.subscription.v3.next.renewal.column" />
              </Typography>
              <Typography className={classes.tableHeaderLabel}>
                <FormattedMessage id="page.subscription.v3.billing.cycle.column" />
              </Typography>
              <Typography className={classes.tableHeaderLabel}>
                <FormattedMessage id="page.subscription.v3.auto.renew.column" />
              </Typography>
              <span />
            </Box>

            {subscriptionGroups.map(subscriptionGroup => (
              <SubscriptionTableRow
                key={getPrimarySubscription(subscriptionGroup).id}
                subscriptions={subscriptionGroup}
                accountManaged={accountManaged}
                trackSubscriptionAction={trackSubscriptionAction}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const SubscriptionV3 = (
  { subscriptions, accountId }: { subscriptions: Subscription[]; accountId: number },
) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const user = useSelector(fromAuth.selectUser);
  const account = useSelector(fromAccounts.selectSelectedAccount);
  const trackingCookies = useGetTrackingCookies();

  const fetchResource = async () => {
    if (accountId) {
      await Promise.all([
        dispatch(fromAccounts.doGetSalesforceLinkedAccount()),
      ]);
    }
  };

  return (
    <Page
      id="subscription"
      headerId="page.subscription.header"
      headerValue={{ userName: user?.firstName }}
      hideHeader
      fetchData={fetchResource}
    >
      <Box className={classes.pageHeader} paddingTop={3}>
        <Box>
          <Typography variant="h1" className={classes.pageTitle}>
            <FormattedMessage id="page.subscription.header" />
          </Typography>
          <Typography className={classes.accountLine}>
            <span className={classes.accountName}>{account?.name}</span>
            {' · '}
            <FormattedMessage
              id="page.subscription.v3.account.id"
              values={{ accountId: account?.id ?? accountId }}
            />
          </Typography>
        </Box>
        <ButtonToLicenseManagement
          title="page.subscription.v3.assign.licenses.btn"
          width="auto"
          height={40}
          fontWeight={700}
          fontSize={13.5}
          borderRadius="10px"
          paddingX="18px"
          disableTextTransform
          backgroundColor="#0F8461"
          hoverBackgroundColor="#0B6B4E"
          startIcon={<PeopleAltOutlinedIcon sx={{ fontSize: 17 }} />}
          endIcon={<ArrowOutwardIcon sx={{ fontSize: 15 }} />}
          onClick={() => sendTrackingData(
            'subscription_page_all_action',
            accountId,
            undefined,
            {
              field: 'subscription_header',
              object: 'assign_licenses',
              action: 'click',
              plan_flow: 'true_bundle',
              ...trackingCookies,
            },
          )}
        />
      </Box>
      <SubscriptionV3Content trueSubscriptions={subscriptions} accountId={accountId} />
    </Page>
  );
};
